import { AnyNode } from "@p0security/shared/types/assessment/data/index";
import { Button, Modal } from "antd";
import { useFlags } from "launchdarkly-react-client-sdk";
import { ReactNode, useCallback, useMemo, useState } from "react";

import { MonitorActionProps } from "../pages/MonitorResults";
import { FindingsFixOrRevertCommands } from "./FindingsFixOrRevertCommands";
import { FindingsFixOrRevertDemo } from "./FindingsFixOrRevertDemo";

/** Displays commands used to fix findings / revert fixes */
export const FindingsFixOrRevertModal: React.FC<MonitorActionProps> = ({
  monitor,
  state,
  allNodes,
  selectedNodes,
}) => {
  const { postureToJit } = useFlags();
  const [modalContent, setModalContent] = useState<ReactNode>();
  const showFix = useCallback(
    (nodes?: AnyNode[]) => () => {
      if (!nodes) return;
      // Render a demo that allows creating a JIT routing rule if the flag is enabled and this is a GCP monitor that supports it
      const fixContent =
        postureToJit && monitor?.fix?.gcloud?.generateRoutingRule ? (
          <FindingsFixOrRevertDemo
            monitor={monitor}
            nodes={nodes}
            findingState={state}
          />
        ) : (
          <FindingsFixOrRevertCommands
            monitor={monitor}
            nodes={nodes}
            findingState={state}
          />
        );
      setModalContent(fixContent);
    },
    [state, monitor, postureToJit]
  );

  const onFix = useMemo(
    () => showFix(selectedNodes?.length ? selectedNodes : allNodes),
    [allNodes, selectedNodes, showFix]
  );
  const onCancelFix = useCallback(() => setModalContent(undefined), []);

  const label = state === "open" ? "🔧\u00a0Review fix" : "↩️\u00a0Revert";

  return (
    <>
      <Button disabled={!allNodes?.length} onClick={onFix}>
        {label}
      </Button>
      <Modal
        maskClosable={false}
        open={!!modalContent}
        onCancel={onCancelFix}
        width={"60em"} // Similar to a terminal window
        footer={false}
      >
        {modalContent}
      </Modal>
    </>
  );
};
